import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Search, User, Loader2 } from 'lucide-react';
import brain from 'brain';
import { useImpersonationStore, ImpersonatedUser } from 'utils/useImpersonationStore';
import { toast } from 'sonner';

interface ActAsUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
  photoURL?: string;
}

export default function ActAsUserDialog({ open, onOpenChange }: ActAsUserDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(false);
  const { setImpersonatedUser } = useImpersonationStore();

  useEffect(() => {
    if (open) {
      fetchStaffList();
    }
  }, [open]);

  const fetchStaffList = async () => {
    setLoading(true);
    try {
      const [staffResp, guardianResp] = await Promise.allSettled([
        brain.list_staff_members(),
        (async () => {
          const { getAuth } = await import('firebase/auth');
          const token = await getAuth().currentUser?.getIdToken();
          return fetch('/api/guardian/admin/list', { headers: { Authorization: `Bearer ${token}` } });
        })(),
      ]);
      let merged: StaffMember[] = [];
      if (staffResp.status === 'fulfilled' && staffResp.value.ok) {
        const data = await staffResp.value.json();
        const staffArray = data.staff_members || data.staffMembers || data.staff || [];
        if (Array.isArray(staffArray)) merged = staffArray;
      }
      if (guardianResp.status === 'fulfilled' && guardianResp.value.ok) {
        const gdata = await guardianResp.value.json();
        const guardians = (gdata.guardians || [])
          .filter((g: any) => g.isActive !== false)
          .map((g: any) => ({
            id: g.id,
            firstName: g.name || g.email || 'Guardian',
            lastName: '',
            email: g.email || '',
            role: 'guardian',
          }));
        merged = [...merged, ...guardians];
      }
      setStaffList(merged);
    } catch (error) {
      console.error('Error fetching user list:', error);
      toast.error('Failed to load user list');
    } finally {
      setLoading(false);
    }
  };

  const filteredStaff = staffList.filter(staff => {
    const fullName = `${staff.firstName} ${staff.lastName}`.toLowerCase();
    const email = (staff.email || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || email.includes(search);
  });

  const handleSelectUser = (staff: StaffMember) => {
    const impersonatedUser: ImpersonatedUser = {
      id: staff.id,
      firstName: staff.firstName,
      lastName: staff.lastName,
      email: staff.email,
      role: staff.role,
    };
    
    setImpersonatedUser(impersonatedUser);
    toast.success(`Now acting as ${staff.firstName} ${staff.lastName}`.trim());
    onOpenChange(false);
    
    // Guardians land on their portal; everyone else reloads in place.
    if (staff.role === 'guardian') {
      window.location.href = '/guardian';
    } else {
      window.location.reload();
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Act as User
          </DialogTitle>
          <DialogDescription>
            Select a staff member or guardian to view the app as them. Guardians open in the Family &amp; Guardian Portal.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <ScrollArea className="h-[300px] border rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : filteredStaff.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                {searchTerm ? 'No matching staff found' : 'No staff members'}
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {filteredStaff.map((staff) => (
                  <button
                    key={staff.id}
                    onClick={() => handleSelectUser(staff)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-left"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-teal-100 text-teal-700">
                        {getInitials(staff.firstName, staff.lastName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate flex items-center gap-2">
                        {staff.firstName} {staff.lastName}
                        {staff.role === 'guardian' && (
                          <span className="rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-teal-700" data-testid={`act-as-guardian-badge-${staff.id}`}>
                            Guardian
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {staff.email}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
